import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getRecipe } from '../data/recipes';
import { Printer, Leaf, ArrowLeft } from 'lucide-react';

const PrintRitualCard = () => {
  const { id } = useParams();
  const recipe = getRecipe(id);

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  useEffect(() => {
    // Auto-open the print dialog after a short delay so images load
    if (recipe) {
      const t = setTimeout(() => { try { window.print(); } catch (e) { console.warn('Print dialog failed to open', e); } }, 600);
      return () => clearTimeout(t);
    }
  }, [recipe]);

  if (!recipe) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif-elegant text-3xl mb-4">Ritual not found</h2>
        <Link to="/recipes" className="btn-primary">Back to Recipe Journal</Link>
      </div>
    );
  }

  return (
    <div className="print-card min-h-screen bg-[#faf7f1] py-10 px-6">
      {/* Toolbar (screen only) */}
      <div className="no-print max-w-3xl mx-auto flex items-center justify-between mb-6">
        <Link to={`/recipes/${recipe.id}`} className="inline-flex items-center gap-1 text-sm text-[#6f6a5f] hover:text-[#3a4a3b]">
          <ArrowLeft className="w-4 h-4" /> Back to ritual
        </Link>
        <button onClick={() => window.print()} className="btn-primary inline-flex items-center gap-2">
          <Printer className="w-4 h-4" /> Print card
        </button>
      </div>

      {/* The card itself */}
      <article className="max-w-3xl mx-auto bg-white border border-[#e5decf] shadow-sm print:shadow-none print:border-0 p-10 md:p-14">
        <header className="text-center pb-6 border-b border-[#c9c1ad]">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-[10px] text-[#7a8b6b] mb-3">
            <Leaf className="w-3 h-3" /> Herbs We Love &middot; Ritual Card
          </div>
          <h1 className="font-serif-elegant text-4xl md:text-5xl leading-tight">{recipe.title}</h1>
          <p className="font-script text-xl text-[#7a8b6b] mt-2">{recipe.subtitle}</p>
          <div className="flex justify-center gap-4 mt-4 text-[11px] uppercase tracking-widest text-[#6f6a5f]">
            <span>{recipe.time}</span>
            <span>&middot;</span>
            <span>{recipe.difficulty}</span>
            <span>&middot;</span>
            <span>{recipe.moment}</span>
          </div>
        </header>

        <p className="text-[#4a4640] leading-relaxed italic text-center mt-6 mb-8">{recipe.intro}</p>

        <div className="grid md:grid-cols-[1fr_1.5fr] gap-8 md:gap-12">
          {/* Ingredients */}
          <section>
            <h2 className="uppercase tracking-widest text-xs text-[#7a8b6b] mb-3 border-b border-[#c9c1ad] pb-2">You&rsquo;ll need</h2>
            <ul className="space-y-2">
              {recipe.ingredients.map((ing, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#2f2a24]">
                  <span className="text-[#7a8b6b] mt-0.5">&bull;</span>
                  <span>{ing.text}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Steps */}
          <section>
            <h2 className="uppercase tracking-widest text-xs text-[#7a8b6b] mb-3 border-b border-[#c9c1ad] pb-2">The ritual</h2>
            <ol className="space-y-4">
              {recipe.steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <div className="font-serif-elegant text-2xl text-[#7a8b6b] leading-none w-8 shrink-0">{String(i + 1).padStart(2, '0')}</div>
                  <p className="text-sm text-[#2f2a24] leading-relaxed pt-0.5">{step}</p>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {recipe.tip && (
          <div className="mt-10 border-t border-[#c9c1ad] pt-6">
            <div className="uppercase tracking-widest text-xs text-[#7a8b6b] mb-2">A tender tip</div>
            <p className="text-sm text-[#4a4640] italic leading-relaxed">{recipe.tip}</p>
          </div>
        )}

        <footer className="mt-10 pt-4 border-t border-[#c9c1ad] flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-[#7a8b6b]">
          <span>herbswelove.com</span>
          <span>Herbs. Healing. Heart.</span>
        </footer>
      </article>
    </div>
  );
};

export default PrintRitualCard;
